import { z } from "zod";

import { batchRejectedError, enrichBridgeError } from "./errors.ts";
import { executeOps } from "./office-ops.ts";
import {
  type BridgeError,
  type BridgeResult,
  DEFAULT_RUN_TIMEOUT_MS,
  IframeToParentMessage,
  MAX_LOG_ENTRIES,
  MAX_OPS_PER_BATCH,
  OpSchema,
} from "./protocol.ts";
import { sandboxSource } from "./sandbox-source.ts";

export type RunOptions = {
  signal?: AbortSignal;
  timeoutMs?: number;
};

// `default-src 'none'` blocks everything but inline script (the runner) — no
// network, no fetch, no images. The iframe's only outbound channel is
// postMessage to the parent.
// `unsafe-eval` is required because the runner uses `new Function(code)` to
// execute model-generated code. Safe here: the iframe's null origin already
// blocks DOM/cookie/storage/network access, so eval inside it can't reach
// anything sensitive.
const sandboxHtml = `<!doctype html><html><head><meta charset="utf-8"><meta http-equiv="Content-Security-Policy" content="default-src 'none'; script-src 'unsafe-inline' 'unsafe-eval'; connect-src 'none'"></head><body><script>${sandboxSource}</script></body></html>`;

const LooseBatchMessage = z.object({
  type: z.literal("batch"),
  id: z.string().min(1),
  ops: z.unknown(),
});

const toError = (e: unknown): BridgeError => {
  if (e instanceof Error) {
    return enrichBridgeError({ name: e.name, message: e.message, stack: e.stack });
  }
  return enrichBridgeError({ name: "Error", message: String(e) });
};

const rejectBatch = (
  iframe: HTMLIFrameElement,
  id: string,
  error: BridgeError,
): void => {
  iframe.contentWindow?.postMessage(
    { type: "batchResult", id, ok: false, error },
    "*",
  );
};

export const runInSandbox = (code: string, opts: RunOptions = {}): Promise<BridgeResult> => {
  const timeoutMs = opts.timeoutMs ?? DEFAULT_RUN_TIMEOUT_MS;
  const log: unknown[][] = [];
  let opsCount = 0;
  let settled = false;

  return new Promise<BridgeResult>((resolve) => {
    const iframe = document.createElement("iframe");
    iframe.setAttribute("sandbox", "allow-scripts");
    iframe.setAttribute("aria-hidden", "true");
    iframe.style.display = "none";
    iframe.srcdoc = sandboxHtml;

    let timer: ReturnType<typeof setTimeout> | null = null;

    const cleanup = () => {
      window.removeEventListener("message", onMessage);
      if (timer !== null) clearTimeout(timer);
      if (opts.signal) opts.signal.removeEventListener("abort", onAbort);
      iframe.remove();
    };

    const finishOk = (summary: unknown) => {
      if (settled) return;
      settled = true;
      cleanup();
      resolve({ ok: true, log, opsCount, summary });
    };

    const finishErr = (error: BridgeError) => {
      if (settled) return;
      settled = true;
      cleanup();
      resolve({ ok: false, log, opsCount, error: enrichBridgeError(error) });
    };

    const onMessage = (event: MessageEvent) => {
      if (event.source !== iframe.contentWindow) return;

      // Reject invalid batches immediately (too many ops / bad op shape).
      // Previously Zod failure was a silent drop → pending promise hung until
      // the run timeout, which looked like a mysterious hang to the model.
      const looseBatch = LooseBatchMessage.safeParse(event.data);
      if (looseBatch.success) {
        const { id, ops } = looseBatch.data;
        if (!Array.isArray(ops)) {
          rejectBatch(
            iframe,
            id,
            batchRejectedError("pp.batch expects an array of ops"),
          );
          return;
        }
        if (ops.length > MAX_OPS_PER_BATCH) {
          rejectBatch(
            iframe,
            id,
            batchRejectedError(
              `pp.batch has ${ops.length.toString()} ops; max is ${MAX_OPS_PER_BATCH.toString()}. Split the work or use pp.addShapes for construction.`,
            ),
          );
          return;
        }
        for (let i = 0; i < ops.length; i++) {
          const opParsed = OpSchema.safeParse(ops[i]);
          if (!opParsed.success) {
            const issue = opParsed.error.issues[0];
            const detail = issue
              ? `${issue.path.join(".") || "op"}: ${issue.message}`
              : "invalid op";
            rejectBatch(
              iframe,
              id,
              batchRejectedError(`op[${i.toString()}] ${detail}`),
            );
            return;
          }
        }

        opsCount += ops.length;
        const typedOps = ops.map((op) => OpSchema.parse(op));
        void executeOps(typedOps).then(
          (results) => {
            iframe.contentWindow?.postMessage(
              { type: "batchResult", id, ok: true, results },
              "*",
            );
          },
          (err: unknown) => {
            rejectBatch(iframe, id, toError(err));
          },
        );
        return;
      }

      const parsed = IframeToParentMessage.safeParse(event.data);
      if (!parsed.success) return;
      const msg = parsed.data;
      switch (msg.type) {
        case "ready":
          iframe.contentWindow?.postMessage({ type: "run", code }, "*");
          return;
        case "log":
          if (log.length < MAX_LOG_ENTRIES) log.push(msg.args);
          return;
        case "batch":
          // Handled above via LooseBatchMessage.
          return;
        case "done":
          finishOk(msg.value);
          return;
        case "error":
          finishErr(msg.error);
          return;
      }
    };

    const onAbort = () => {
      finishErr({ name: "AbortError", message: "Run cancelled" });
    };

    timer = setTimeout(() => {
      finishErr({
        name: "BudgetExceeded",
        message: `Run exceeded ${timeoutMs.toString()}ms`,
        code: "BudgetExceeded",
      });
    }, timeoutMs);

    window.addEventListener("message", onMessage);

    if (opts.signal) {
      if (opts.signal.aborted) {
        onAbort();
        return;
      }
      opts.signal.addEventListener("abort", onAbort);
    }

    document.body.appendChild(iframe);
  });
};
